/**
 * Where the public site lives.
 *
 * The legal documents — privacy, terms, data governance — are pages on the public site, not screens
 * in this app. They are the same documents for everybody and they have to be readable by a parent
 * with a registration link who will never sign in here, so there is one copy and it lives there.
 * This app links to it.
 *
 * Derived from the current host rather than baked at build time, for the reason `api-base.ts`
 * spells out at length: a production bundle built without the env var set would otherwise ship
 * links to somebody's laptop. The app and the site are always siblings in the same zone —
 *
 *   - `app.pow-wow.ca`     → `pow-wow.ca`      (the apex)
 *   - `app-uat.pow-wow.ca` → `uat.pow-wow.ca`  (the suffix travels, as it does for the API)
 *   - `app-dev.pow-wow.ca` → `dev.pow-wow.ca`
 *
 * — and locally there is usually an Astro dev server on :4321.
 */
export const SITE_BASE_URL = resolveSiteBaseUrl();

function resolveSiteBaseUrl(): string {
  const configured = import.meta.env.VITE_SITE_URL;
  if (configured) return configured;

  if (typeof window !== "undefined") {
    const { hostname, protocol } = window.location;
    if (isLoopback(hostname)) return "http://localhost:4321";

    // `app.` with no suffix is the apex, so the label goes entirely; `app-uat.` becomes `uat.`.
    const match = hostname.match(/^(?:app|www)(?:-([a-z0-9]+))?\.(.+)$/);
    if (match) {
      const [, suffix, zone] = match;
      return `${protocol}//${suffix ? `${suffix}.${zone}` : zone}`;
    }
    // Not an `app.` host at all — a community hostname, or something unexpected. Its own origin
    // serves the same legal pages, so staying put is both correct and the safest guess.
    return `${protocol}//${hostname}`;
  }

  return "http://localhost:4321";
}

function isLoopback(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}
